import { Buildings } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLogout, useSwitchWorkspace, useWorkspaces } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";

/** /logout — ends the session, then hands over to the login route. */
export function LogoutScene() {
  const { t } = useTranslation();
  const logout = useLogout();
  const navigate = useNavigate();
  useDocumentTitle(t("Signing out"));

  const { mutate } = logout;
  useEffect(() => {
    mutate(undefined, {
      onSettled: () => void navigate({ to: "/login" }),
    });
  }, [mutate, navigate]);

  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-3">
      <Spinner label={t("Signing out")} />
      <Text variant="secondary">{t("Signing out…")}</Text>
    </div>
  );
}

/**
 * /auth/callback — where an identity provider returns to. Today it only forwards; the
 * seam is here so adding a real IdP is a change to this file and nothing else.
 */
export function AuthCallbackScene() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  useDocumentTitle(t("Signing in"));

  useEffect(() => {
    // SLATE-LATER (§15): exchange the authorisation code before redirecting.
    const timer = setTimeout(() => void navigate({ to: "/" }), 300);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="flex h-dvh flex-col items-center justify-center gap-3">
      <Spinner label={t("Signing in")} />
      <Text variant="secondary">{t("Completing sign-in…")}</Text>
    </div>
  );
}

/** /select-workspace — which tenant to enter, when a viewer belongs to more than one. */
export function SelectWorkspaceScene() {
  const { t } = useTranslation();
  const { data: workspaces, isLoading } = useWorkspaces();
  const switchWorkspace = useSwitchWorkspace();
  const navigate = useNavigate();
  useDocumentTitle(t("Choose a workspace"));

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-4">
      <div className="flex w-[360px] max-w-full flex-col gap-4 rounded-md border border-divider bg-menu-bg p-6 shadow-menu">
        <div>
          <Text as="h1" variant="h3">
            {t("Choose a workspace")}
          </Text>
          <Text variant="secondary">{t("You have access to more than one.")}</Text>
        </div>

        {isLoading ? (
          <Spinner label={t("Loading")} />
        ) : !workspaces?.length ? (
          <Empty icon={Buildings} message={t("No workspaces available")} />
        ) : (
          <ul className="flex flex-col gap-2">
            {workspaces.map((workspace) => (
              <li key={workspace.id}>
                <Button
                  fullWidth
                  icon={Buildings}
                  className="justify-start"
                  disabled={workspace.suspended || switchWorkspace.isPending}
                  onClick={() => {
                    if (workspace.current) {
                      void navigate({ to: "/" });
                      return;
                    }
                    switchWorkspace.mutate({ workspaceId: workspace.id });
                  }}
                >
                  {workspace.name}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
